import React, { useCallback, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';

import {
  GetExamDto,
  GetExamArticleDtoTypeEnum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { HarmfulFactorsSupportingExaminationInfo } from '../TestGiving/HarmfulFactorsSupportingExaminationInfo';

import { SynevoCostSummary } from './SynevoCostSummary';
import { SynevoSearch } from './SynevoSearch';
import { SynevoOrderModal } from './SynevoOrderModal';

interface ExamSynevoTabProps {
  exam: GetExamDto;
  refresh: boolean;
  onRefresh: () => void;
}

export const ExamSynevoTab: React.FC<ExamSynevoTabProps> = ({
  exam,
  refresh,
  onRefresh,
}) => {
  const spd = useSpdCore();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const getData = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      spd.synevo.getForExamByPortion(
        exam.id,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        onRefresh,
      ),
    [spd.synevo, exam.id, onRefresh],
  );

  const getCount = useCallback(
    (filterText?: string) => spd.synevo.getForExamCount(exam.id, filterText),
    [spd.synevo, exam.id],
  );

  const hasSynevoSupportingExaminations = useMemo(() => {
    const overrideIds = exam.articleHarmfulFactorSynevoOverrideDictIds;
    if (Array.isArray(overrideIds)) {
      return overrideIds.length > 0;
    }
    return (exam.harmulFactorsEntities || []).some(factor =>
      (factor.harmfulFactorSupports || []).some(
        support => !!support.supportingExaminationDict?.synevoCode,
      ),
    );
  }, [
    exam.harmulFactorsEntities,
    exam.articleHarmfulFactorSynevoOverrideDictIds,
  ]);

  const handleConfirm = async (supportingExaminationDictIds: string[]) => {
    setIsLoading(true);
    try {
      await spd.synevo.createForExam(exam.id, supportingExaminationDictIds);
      setShowModal(false);
      onRefresh();
    } catch (error) {
      const errors = await responseErrors(error as ResponseError);
      setSaveErrors(errors);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <HarmfulFactorsSupportingExaminationInfo
        harmfulFactorsIds={exam.harmfulFactorsIds}
        articleDoctorsIds={(exam.examArticles || [])
          .filter(ea => ea.type === GetExamArticleDtoTypeEnum.Doctor)
          .map(ea => ea.articleId)}
        articlePsychologistsIds={(exam.examArticles || [])
          .filter(ea => ea.type === GetExamArticleDtoTypeEnum.Psychologist)
          .map(ea => ea.articleId)}
      />

      <SynevoCostSummary examId={exam.id} refresh={refresh} />

      {hasSynevoSupportingExaminations && (
        <>
          <Button
            className="mb-3"
            variant="outline-primary"
            disabled={isLoading}
            onClick={() => {
              setSaveErrors([]);
              setShowModal(true);
            }}
          >
            Utwórz zlecenie Synevo
          </Button>
          <SynevoOrderModal
            show={showModal}
            exam={exam}
            isLoading={isLoading}
            errors={saveErrors}
            onConfirm={handleConfirm}
            onHide={() => {
              setShowModal(false);
              setSaveErrors([]);
            }}
          />
        </>
      )}

      <SynevoSearch getData={getData} getCount={getCount} refresh={refresh} />
    </>
  );
};
